import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Paper, Stack, Typography, Chip, Divider } from "@mui/material";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import { getDashboard } from "../../../redux/features/careerSlice";

const label = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// one big headline stat
const StatCard = ({ label: l, value, hint, color }) => (
  <Grid item xs={6} sm={6} md={3}>
    <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
      <Typography variant="h3" fontWeight={700} sx={{ color }}>{value ?? 0}</Typography>
      <Typography variant="subtitle2">{l}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  </Grid>
);

// Officer dashboard — simple, at-a-glance totals.
const CareerDashboard = () => {
  const dispatch = useDispatch();
  const { dashboard, dashboardLoading } = useSelector((state) => state.career);

  useEffect(() => {
    dispatch(getDashboard());
  }, [dispatch]);

  const d = dashboard || {};
  const jobs = d.jobs || {};
  const byStatus = d.byStatus || {};

  return (
    <GlobalWrapper title={"Career Connect Dashboard"}>
      {dashboardLoading && !dashboard ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : (
        <Box sx={{ width: "100%" }}>
          {/* the basics, at a glance */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <StatCard label="Total Jobs" value={jobs.total} color="primary.main" hint="all posted jobs" />
            <StatCard label="Total Applied" value={d.applied} color="warning.main" hint="students who applied" />
            <StatCard label="Total Vacancies" value={d.totalVacancies} color="info.main" hint="openings in live jobs" />
            <StatCard label="Placed" value={d.placed} color="success.main" hint="joined a company" />
          </Grid>

          <Grid container spacing={2}>
            {/* jobs breakdown */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700}>Jobs</Typography>
                <Typography variant="caption" color="text.secondary">
                  Published + Closed + Draft = Total Jobs ({jobs.total || 0})
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  <Chip color="success" variant="outlined" label={`Published: ${jobs.published || 0}`} />
                  <Chip variant="outlined" label={`Closed: ${jobs.closed || 0}`} />
                  <Chip variant="outlined" label={`Draft: ${jobs.draft || 0}`} />
                </Stack>
              </Paper>
            </Grid>

            {/* applicants by current stage */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700}>Applicants by stage</Typography>
                <Typography variant="caption" color="text.secondary">
                  Each applicant&apos;s current status (not a running total)
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                {Object.keys(byStatus).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No applications yet.</Typography>
                ) : (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                    {Object.entries(byStatus).map(([k, v]) => (
                      <Chip key={k} variant="outlined" label={`${label(k)}: ${v}`} />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </GlobalWrapper>
  );
};

export default CareerDashboard;
